import React from "react";
import type { TElement, TText } from "platejs";

export const CortesEditorContext = React.createContext<{
  onNavigateSource?: (sourceId: string, itemId: string, itemType: string) => void;
  onTimecodeClick?: (item: any) => void;
  activeMockTc?: string;
} | null>(null);

export const BLOCK_LOG = "cortes-log";
export const BLOCK_OTF = "cortes-otf";
export const BLOCK_TRANSCRIPT = "cortes-transcript";
export const BLOCK_CHECKLIST = "cortes-checklist";
// User-insertable text blocks.
export const BLOCK_PLECA = "cortes-pleca";
export const BLOCK_NOTA = "cortes-nota";

export interface LogBlockElement extends TElement {
  type: typeof BLOCK_LOG;
  sourceId?: string;
  tc?: string;
  author?: string;
}

export interface OtfBlockElement extends TElement {
  type: typeof BLOCK_OTF;
  sourceId?: string;
  speakerName?: string;
  // Set cuando OTF MODE promovió un log o transcript a OTF en el drop.
  // Los exports (TXT/PDF/DOC) lo usan para mostrar "[OTF←LOG]" / "[OTF←TRANS]"
  // en vez de un "[OTF]" genérico. Bloques nacidos como OTF no lo llevan.
  originType?: "log" | "transcript" | "transcription";
}

export interface TranscriptBlockElement extends TElement {
  type: typeof BLOCK_TRANSCRIPT;
  sourceId?: string;
  speakerName?: string;
}

export interface ChecklistItem {
  id: string;
  label: string;
  done: boolean;
}

export interface ChecklistBlockElement extends TElement {
  type: typeof BLOCK_CHECKLIST;
  items: ChecklistItem[];
}

export type CortesNode =
  | LogBlockElement
  | OtfBlockElement
  | TranscriptBlockElement
  | ChecklistBlockElement
  | TElement;

export interface ValidationResult {
  ok: boolean;
  faltantes: Array<{ blockType: string; titulo: string; motivo: string }>;
}

export function nodeText(node: TElement): string {
  return (node.children as TText[])
    .map((c) => (typeof c?.text === "string" ? c.text : ""))
    .join("")
    .trim();
}

export function isDocumentoValido(value: TElement[]): ValidationResult {
  const faltantes: ValidationResult["faltantes"] = [];

  for (const node of value) {
    if (node.type === BLOCK_LOG || node.type === BLOCK_OTF || node.type === BLOCK_TRANSCRIPT) {
      const text = nodeText(node);
      if (!text) {
        faltantes.push({
          blockType: node.type,
          titulo: "(bloque sin texto)",
          motivo: "texto vacío",
        });
      }
    } else if (node.type === BLOCK_CHECKLIST) {
      const b = node as ChecklistBlockElement;
      const pendientes = b.items.filter((i) => !i.done);
      if (pendientes.length > 0) {
        faltantes.push({
          blockType: node.type,
          titulo: "Checklist de cierre",
          motivo: `${pendientes.length} item(s) pendientes`,
        });
      }
    }
  }

  return { ok: faltantes.length === 0, faltantes };
}

export const DEFAULT_CHECKLIST_ITEMS: ChecklistItem[] = [
  { id: "duracion", label: "Aprobar duración total", done: false },
  { id: "editor", label: "Asignar editor responsable", done: false },
  { id: "vtr", label: "Confirmar destino VTR", done: false },
];
